import { useContext, useEffect, useMemo, useState } from "react";

import {
  searchInsuranceOfferPractice,
  searchInsuranceOfferType,
} from "@datanac/datanac-api-toolkit/dist/utility/InsuranceOfferHelper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { Box, Button, Card, CardContent, Divider, FormControl, Grid, Stack, Typography } from "@mui/material";
import { AppContext } from "App";
import DataFormSelect from "components/Core/DataFormSelect";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import TilleyFormSelect from "components/Core/TilleyFormSelect";
import { CalculatorIcon } from "components/Icons/Icons";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { useSnackbar } from "notistack";
import { formatNumber, formatPrice } from "utility/formatters";

import { RmaApiHelper } from "../../api/ApiHelper";
import { useSoybeanScnCalculator, VALIDATION_CONSTANTS } from "../../hooks/useSoybeanScnCalculator";

export default function SoybeanScnRiskCalculatorWorkspace() {
  const { globalState } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const [results, setResults] = useState(null);

  // Use the custom hook for all state management and data loading
  const {
    ssurgo,
    countyData,
    price,
    femaleIndex,
    soil_sand_average,
    soil_ph_average,
    yield_potential: hookYieldPotential,
    projected_price: hookProjectedPrice,
    isLoading,
    isSsurgoLoading,
    state,
    handleFieldChange,
    resetState,
    validateField,
    actions: { loadSsurgoData, calculateScnSoybeanRisk },
  } = useSoybeanScnCalculator();

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
      preventDuplicate: true,
      variant: "error",
    });
  };

  const handleChangeState = (event, value) => {
    if (!event?.target) return;

    const eventValue = event.target.value;
    const eventName = event.target.name;

    value = value ?? eventValue;

    // Use the hook's handleFieldChange function which handles cascading updates
    handleFieldChange(eventName, value);
  };

  const isFormValid = () => {
    // Check if all required fields are present
    const requiredFields = [
      "scn_egg_count",
      "state_name",
      "county_name",
      "type_name",
      "practice_name",
      "map_unit_name",
      "avg_sand",
      "avg_soil_ph",
      "yield_potential",
      "projected_price",
    ];

    for (const field of requiredFields) {
      if (!state[field]) {
        return false;
      }
    }

    // Check validation for numeric fields
    const fieldsToValidate = [
      "scn_egg_count",
      "female_index",
      "avg_sand",
      "avg_soil_ph",
      "yield_potential",
      "projected_price",
    ];

    for (const field of fieldsToValidate) {
      if (state[field]) {
        const validation = validateField(field, state[field]);
        if (!validation.isValid) {
          return false;
        }
      }
    }

    return true;
  };

  const handleCalculate = async () => {
    if (!isFormValid()) {
      // Check for missing required fields
      const requiredFields = [
        { field: "scn_egg_count", label: "SCN Egg Count" },
        { field: "state_name", label: "State" },
        { field: "county_name", label: "County" },
        { field: "type_name", label: "Type" },
        { field: "practice_name", label: "Practice" },
        { field: "map_unit_name", label: "Map Unit" },
        { field: "avg_sand", label: "Average Sand" },
        { field: "avg_soil_ph", label: "Average Soil pH" },
        { field: "yield_potential", label: "Yield Potential" },
        { field: "projected_price", label: "Projected Price" },
      ];

      const missingFields = requiredFields.filter(({ field }) => !state[field]);

      if (missingFields.length > 0) {
        enqueueSnackbar(`Please fill in: ${missingFields.map((f) => f.label).join(", ")}`, {
          preventDuplicate: true,
          variant: "warning",
        });
        return;
      }

      // Check for validation errors
      const fieldsToValidate = [
        { field: "scn_egg_count", label: "SCN Egg Count" },
        { field: "female_index", label: "Female Index" },
        { field: "avg_sand", label: "Average Sand" },
        { field: "avg_soil_ph", label: "Average Soil pH" },
        { field: "yield_potential", label: "Yield Potential" },
        { field: "projected_price", label: "Projected Price" },
      ];

      for (const { field, label } of fieldsToValidate) {
        if (state[field]) {
          const validation = validateField(field, state[field]);
          if (!validation.isValid) {
            enqueueSnackbar(`${label}: ${validation.error}`, {
              preventDuplicate: true,
              variant: "error",
            });
            return;
          }
        }
      }
    }

    try {
      // Parse input values
      const scnEggCount = parseFloat(state.scn_egg_count);
      const femaleIndexValue = parseFloat(state.female_index) || 30; // Default value if not set
      const avgSand = parseFloat(state.avg_sand);
      const avgSoilPh = parseFloat(state.avg_soil_ph);
      const yieldPotential = parseFloat(state.yield_potential);
      const projectedPrice = parseFloat(state.projected_price);

      // Calculate SCN risk using the hook's function
      const calculation = calculateScnSoybeanRisk({
        scn_egg_count: scnEggCount,
        female_index: femaleIndexValue,
        avg_sand: avgSand,
        avg_soil_ph: avgSoilPh,
        yield_potential: yieldPotential,
        projected_price: projectedPrice,
      });

      // Set results with calculated values
      setResults({
        state_name: state.state_name,
        scn_egg_count: scnEggCount,
        female_index: femaleIndexValue,
        avg_sand: avgSand,
        avg_soil_ph: avgSoilPh,
        yield_loss_percentage: calculation.yield_loss_percentage,
        yield_loss_bushels: calculation.yield_loss_bushels,
        profit_loss: calculation.profit_loss,
      });
    } catch (err) {
      handleError(err);
    }
  };

  const handleReset = () => {
    resetState();
    setResults(null);
  };

  const typeDataFormSelect = useMemo(() => {
    return (
      <DataFormSelect
        value={state.type_name || ""}
        name="type_name"
        isRequired={true}
        onChange={handleChangeState}
        objectNameLabel="Type"
        itemGetKey={(item) => item.type_name}
        itemGetLabel={(item) => item.type_name}
        doFetch={state.state_name && state.county_name && state.commodity_name}
        isLoading={isLoading}
        helperText="Select the insurance type for soybeans"
        customDataLoader={() =>
          searchInsuranceOfferType(
            {
              state_name: state?.state_name,
              county_name: state?.county_name,
              commodity_name: "Soybeans",
            },
            RmaApiHelper,
            String(getCurrentReinsuranceYear())
          )
        }
      />
    );
  }, [state.state_name, state.county_name, state.commodity_name, state.type_name, isLoading, handleChangeState]);

  const practiceDataFormSelect = useMemo(() => {
    return (
      <DataFormSelect
        value={state.practice_name || ""}
        name="practice_name"
        isRequired={true}
        onChange={handleChangeState}
        objectNameLabel="Practice"
        itemGetKey={(item) => item.practice_name}
        itemGetLabel={(item) => item.practice_name}
        doFetch={state.state_name && state.county_name && state.commodity_name && state.type_name}
        isLoading={isLoading}
        helperText="Select the farming practice for soybeans"
        customDataLoader={() =>
          searchInsuranceOfferPractice(
            {
              state_name: state?.state_name,
              county_name: state?.county_name,
              commodity_name: "Soybeans",
              type_name: state.type_name,
            },
            RmaApiHelper,
            String(getCurrentReinsuranceYear())
          )
        }
      />
    );
  }, [
    state.state_name,
    state.county_name,
    state.commodity_name,
    state.type_name,
    state.practice_name,
    isLoading,
    handleChangeState,
  ]);

  // Memoize the item filter for TilleyFormSelect to prevent unnecessary re-renders
  const ssurgoItemFilter = useMemo(
    () => ({
      state_name: state.state_name + " ",
      county_name: state.county_name,
    }),
    [state.state_name, state.county_name]
  );

  // Use SSURGO query function from hook
  const handleSsurgoQuery = loadSsurgoData;

  return (
    <>
      <WorkspaceHeader
        title="Soybean SCN Risk Calculator"
        category={{
          title: "Calculators",
          icon: <CalculatorIcon />,
        }}
      />

      <Box sx={{ width: "100%", maxWidth: "1200px" }}>
        <Grid container spacing={3}>
          {/* Input Form */}
          <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
            <Card className="calculator-card">
              <CardContent>
                {/* Production Section */}
                <Typography variant="h6" sx={{ mt: 2, mb: 2 }} className="calculator-section-title">
                  Production
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <DataFormSelect
                      value={state.state_name || ""}
                      name="state_name"
                      onChange={handleChangeState}
                      objectName="State"
                      objectNameLabel="State"
                      itemFilter={{ reinsurance_year: getCurrentReinsuranceYear() }}
                      itemGetKey={(item) => item.state_name}
                      itemGetLabel={(item) => item.state_name}
                      doFetch={true}
                      isLoading={isLoading}
                      isRequired={true}
                      helperText="State location for the farm operation"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <DataFormSelect
                      value={state.county_name || ""}
                      name="county_name"
                      onChange={handleChangeState}
                      objectName="County"
                      objectNameLabel="County"
                      itemFilter={{
                        reinsurance_year: getCurrentReinsuranceYear(),
                        state_name: state.state_name,
                      }}
                      itemGetKey={(item) => item.county_name}
                      itemGetLabel={(item) => item.county_name}
                      doFetch={state.state_name}
                      isLoading={isLoading}
                      isRequired={true}
                      helperText="County location for the farm operation"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {typeDataFormSelect}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {practiceDataFormSelect}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <StatefulControlledTextField
                        label="Yield Potential (bu/acre)"
                        name="yield_potential"
                        isRequired={true}
                        defaultValue={state.yield_potential || ""}
                        variant="outlined"
                        onChange={handleChangeState}
                        InputLabelProps={{ shrink: true }}
                        maskType={MASK_NUMERIC}
                        helperText="Auto-populated from RMA county data"
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <StatefulControlledTextField
                        label="Projected Price ($/bu)"
                        name="projected_price"
                        isRequired={true}
                        defaultValue={state.projected_price || ""}
                        variant="outlined"
                        onChange={handleChangeState}
                        InputLabelProps={{ shrink: true }}
                        maskType={MASK_NUMERIC}
                        helperText="Auto-populated from RMA price data"
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Soybean Nematode Section */}
                <Typography variant="h6" sx={{ mb: 1 }} className="calculator-section-title">
                  Soybean Nematode
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <StatefulControlledTextField
                        label="Female Index (%)"
                        name="female_index"
                        isRequired={false}
                        defaultValue={state.female_index || ""}
                        variant="outlined"
                        onChange={handleChangeState}
                        InputLabelProps={{ shrink: true }}
                        maskType={MASK_NUMERIC}
                        helperText="Optional: Percentage of females reproducing (defaults by state)"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <StatefulControlledTextField
                        label="SCN Egg Count (eggs/cc)"
                        name="scn_egg_count"
                        isRequired={true}
                        defaultValue={state.scn_egg_count || ""}
                        variant="outlined"
                        onChange={handleChangeState}
                        InputLabelProps={{ shrink: true }}
                        maskType={MASK_NUMERIC}
                        helperText="Number of soybean cyst nematode eggs per cubic centimeter"
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Soil Section */}
                <Typography variant="h6" sx={{ mb: 1 }} className="calculator-section-title">
                  Soil
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <TilleyFormSelect
                      value={state.map_unit_name || ""}
                      name="map_unit_name"
                      onChange={handleChangeState}
                      objectName="Map Unit"
                      itemFilter={ssurgoItemFilter}
                      itemGetKey={(item) => item.map_unit_name}
                      itemGetLabel={(item) => item.map_unit_name}
                      doFetch={state.state_name && state.county_name}
                      isLoading={isSsurgoLoading}
                      isRequired={true}
                      helperText="Select the soil map unit for the farm location"
                      query={handleSsurgoQuery}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <StatefulControlledTextField
                        label="Average Sand (%)"
                        name="avg_sand"
                        isRequired={true}
                        defaultValue={state.avg_sand ? formatNumber(state.avg_sand, 1) : ""}
                        variant="outlined"
                        onChange={handleChangeState}
                        InputLabelProps={{ shrink: true }}
                        maskType={MASK_NUMERIC}
                        helperText="Auto-populated from SSURGO soil data"
                        InputProps={{ readOnly: true }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <StatefulControlledTextField
                        label="Average Soil pH"
                        name="avg_soil_ph"
                        isRequired={true}
                        defaultValue={state.avg_soil_ph ? formatNumber(state.avg_soil_ph, 2) : ""}
                        variant="outlined"
                        onChange={handleChangeState}
                        InputLabelProps={{ shrink: true }}
                        maskType={MASK_NUMERIC}
                        helperText="Auto-populated from SSURGO soil data"
                        InputProps={{ readOnly: true }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} color="primary" />

                <Box display="flex" justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" onClick={handleReset} disabled={isLoading}>
                      Reset
                    </Button>
                    <Button variant="contained" onClick={handleCalculate} disabled={!isFormValid() || isLoading}>
                      {isLoading ? "Calculating..." : "Calculate Risk"}
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Results */}
          <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }}>
            <Card className="soybean-scn-calculator">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Risk Assessment Results
                </Typography>

                {!results ? (
                  <Typography variant="body2" color="text.secondary">
                    Enter parameters and click "Calculate Risk" to see results
                  </Typography>
                ) : (
                  <>
                    <Box className="input-summary">
                      {/* Input Summary */}
                      <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                        Production
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            State:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" className="input-summary-value">
                            {results.state_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            SCN Egg Count:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" className="input-summary-value">
                            {formatNumber(results.scn_egg_count)} eggs/cc
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Female Index:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" className="input-summary-value">
                            {formatNumber(results.female_index)}%
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Average Sand:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" className="input-summary-value">
                            {formatNumber(results.avg_sand)}%
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Average Soil pH:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" className="input-summary-value">
                            {formatNumber(results.avg_soil_ph, 2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Risk Assessment */}
                    <Box className="risk-assessment">
                      <Typography variant="h5" sx={{ mb: 1 }} color="error.dark">
                        Risk Assessment
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Yield Loss:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="h6" color="error.dark" className="risk-assessment-value">
                            {formatNumber(results.yield_loss_percentage, 1)}%
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Bushels Lost:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="h6" className="risk-assessment-value">
                            {formatNumber(results.yield_loss_bushels, 1)} bu/acre
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Profit Loss:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="h6" color="error.dark" className="risk-assessment-value">
                            {formatPrice(results.profit_loss, 2)}/acre
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
